import { IInferenceModel, useTranslation } from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { ButtonSize, ButtonVariant, IconVariants, Modal, ModalSize } from "@next-platform/client-common-spicy-ui";

import { EXTERNAL_ROUTES, EXTERNAL_ROUTES_FOR_APPLICATION } from "@/constants";
import ModelCard from "@/pages/Chat/components/ModalSelectModel/components/ModelCard";
import { IModalSelectModelProps } from "@/pages/Chat/components/ModalSelectModel/types";
import useModelSelection from "@/pages/Chat/hooks/useModelSelection";
import { cn } from "@/utils";

export default function ModalSelectModel({ isOpen, setIsOpen, onModelChange, currentModelId, dataTestId = "" }: IModalSelectModelProps) {
  const { t } = useTranslation();
  const applicationId = basicAppConfig.applicationId;
  const aiModelParametersDocUrl =
    EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.docs + EXTERNAL_ROUTES.aiModelParameters;

  const { modelsToDisplay, selectedModelId, disabledKeys, isDisabledModelSelected, changeModel, handleModelConfirmation, isSetModelDisabled } =
    useModelSelection({
      setIsOpen,
      isOpen,
      onModelChange,
      currentModelId,
    });

  return (
    <Modal
      className="dark:!bg-gray-6 gap-md"
      classNames={{
        content: "!gap-0 custom-scroll pr-md",
      }}
      title={t("common:modal.selectModel.title")}
      titleInfo={aiModelParametersDocUrl}
      open={isOpen}
      onOpenChange={setIsOpen}
      size={ModalSize.LARGE}
      actions={[
        {
          label: t("common:cancel"),
          variant: ButtonVariant.OUTLINED,
          onPress: () => setIsOpen(false),
          size: ButtonSize.LARGE,
        },
        {
          label: isDisabledModelSelected ? t("common:upgrade") : t("common:modal.selectModel.cta.setModel"),
          iconStart: isDisabledModelSelected ? IconVariants.GEM : undefined,
          onPress: handleModelConfirmation,
          size: ButtonSize.LARGE,
          className: cn(isDisabledModelSelected && "bg-gradient-to-r from-purple-9 to-blue-9 !border-transparent gap-lg transition-none"),
          disabled: isSetModelDisabled,
        },
      ]}
    >
      {modelsToDisplay.map((model: IInferenceModel) => (
        <ModelCard
          key={model.id}
          model={model}
          selectedModel={selectedModelId}
          onChangeModel={changeModel}
          isDisabled={disabledKeys.includes(model.id)}
          dataTestId={dataTestId}
        />
      ))}
    </Modal>
  );
}
